/* eslint-disable @typescript-eslint/no-explicit-any */

import { useCallback } from 'react';
import { useFormContext } from '../contexts/useFormContext';
import {
  validatePaymentRowForAdd,
  validatePaymentRowForUpdate,
  createValidationContext,
} from '../utils/paymentValidation';
import {
  applyOvertimeWorkedRowAutomation,
  OVERTIME_WORKED_GRID_KEY,
  validateOvertimeWorkedRow,
} from '../components/container/forms/schemas/OvertimeApproval/utils';
import {
  applyPartyModalFormState,
  buildPartyDetailsAddMoreFormUpdate,
  getPartyDetailRows,
  isPartyDetailsGridField,
  validateAndStampPartyDetailRow,
} from '../components/container/forms/schemas/GiftHospitalityDonation/partyDetails';

export const useTableOperations = () => {
  const { formState, updateFormData } = useFormContext();

  const processTableField = useCallback(
    (field: any) => {
      if (field.type === 'table') {
        const isPartyDetailsTable = isPartyDetailsGridField(field.name);

        const readCurrentRows = (): unknown[] => {
          let currentData = formState.data[field.name] || [];
          if (
            currentData &&
            typeof currentData === 'object' &&
            'data' in currentData
          ) {
            currentData = currentData.data || [];
          }

          return Array.isArray(currentData)
            ? currentData
            : getPartyDetailRows(currentData);
        };

        const asPartyRow = (value: unknown): Record<string, unknown> | null =>
          typeof value === 'object' && value !== null && !Array.isArray(value)
            ? (value as Record<string, unknown>)
            : null;

        const preparePartyRow = (
          value: unknown
        ): ReturnType<typeof validateAndStampPartyDetailRow> => {
          const partyRow = asPartyRow(value);
          if (!partyRow) {
            return {
              success: false,
              errors: {
                givenOrReceivedBy:
                  'Who was this given or received by? is required',
              },
            };
          }

          // Keep the matching detail fields visible when validation fails so
          // FormDataTable can show the field-level error messages.
          applyPartyModalFormState(
            field.extras?.table?.tableFormSchema,
            partyRow.givenOrReceivedBy
          );

          return validateAndStampPartyDetailRow(partyRow);
        };

        const syncPartyDetailsAddMore = (
          rows: unknown[]
        ): Record<string, unknown> => {
          const partyRows = getPartyDetailRows(rows);
          const latestAnswer =
            partyRows[partyRows.length - 1]?.additionalClientDetailsRequired;

          if (latestAnswer === undefined) {
            return {};
          }

          return buildPartyDetailsAddMoreFormUpdate(latestAnswer);
        };

        return {
          ...field,
          value: formState.data[field.name] || [],
          extras: {
            ...field.extras,
            table: {
              ...field.extras?.table,
              data: isPartyDetailsTable
                ? readCurrentRows()
                : formState.data[field.name] || [],
              onAddRow: (newRow: any, event?: React.SyntheticEvent) => {
                try {
                  console.log('🔍 onAddRow called with:', { newRow, event });

                  if (isPartyDetailsTable) {
                    const prepared = preparePartyRow(newRow);
                    if (!prepared.success) {
                      return prepared;
                    }
                    Object.assign(newRow, prepared.data);
                  }

                  if (field.name === OVERTIME_WORKED_GRID_KEY) {
                    applyOvertimeWorkedRowAutomation(newRow);
                    const overtimeValidation = validateOvertimeWorkedRow(newRow);
                    if (!overtimeValidation.success) {
                      return overtimeValidation;
                    }
                  }

                  const usesPaymentRowValidation = field.name === 'gridData';
                  if (usesPaymentRowValidation) {
                    // Use centralized validation
                    const validationContext = createValidationContext(formState, {
                      gridData: formState.data[field.name],
                    });
                    const validationResult = validatePaymentRowForAdd(
                      newRow,
                      validationContext
                    );

                    console.log(
                      '🔍 Centralized validation result:',
                      validationResult
                    );

                    if (!validationResult.success) {
                      // Validation failed - show errors and prevent adding row
                      console.warn(
                        '❌ Row validation failed:',
                        validationResult.errors
                      );
                      return validationResult; // Return the validation result with errors
                    }

                    // Validation passed - use the validated data
                    newRow = validationResult.data || newRow;
                    console.log(
                      '✅ Validation passed, using validated data:',
                      newRow
                    );
                  }

                  // Get current data - handle both array and object formats
                  const currentData = readCurrentRows();

                  const updatedData = [...currentData, newRow];

                  console.log('📊 Table onAddRow - updating form data:', {
                    fieldName: field.name,
                    currentData,
                    newRow,
                    updatedData,
                    updateStructure: { [field.name]: { data: updatedData } },
                  });

                  // Update form data - use the format expected by calculations
                  updateFormData({
                    [field.name]: { data: updatedData },
                    ...(isPartyDetailsTable
                      ? syncPartyDetailsAddMore(updatedData)
                      : {}),
                  });

                  console.log('✅ Table data updated (add):', updatedData);
                } catch (error) {
                  console.error('❌ Error in onAddRow:', error);
                }
              },
              onUpdateRow: (index: number, updatedRow: any) => {
                try {
                  console.log('🔍 onUpdateRow called with:', {
                    index,
                    updatedRow,
                  });

                  if (isPartyDetailsTable) {
                    const prepared = preparePartyRow(updatedRow);
                    if (!prepared.success) {
                      return prepared;
                    }
                    Object.assign(updatedRow, prepared.data);
                  }

                  if (field.name === OVERTIME_WORKED_GRID_KEY) {
                    applyOvertimeWorkedRowAutomation(updatedRow);
                    const overtimeValidation =
                      validateOvertimeWorkedRow(updatedRow);
                    if (!overtimeValidation.success) {
                      return overtimeValidation;
                    }
                  }

                  const usesPaymentRowValidation = field.name === 'gridData';
                  if (usesPaymentRowValidation) {
                    // Use centralized validation
                    const validationContext = createValidationContext(formState, {
                      gridData: formState.data[field.name],
                    });
                    const validationResult = validatePaymentRowForUpdate(
                      updatedRow,
                      index,
                      validationContext
                    );

                    console.log(
                      '🔍 Centralized validation result:',
                      validationResult
                    );

                    if (!validationResult.success) {
                      // Validation failed - show errors and prevent updating row
                      console.warn(
                        '❌ Row validation failed:',
                        validationResult.errors
                      );
                      return validationResult; // Return the validation result with errors
                    }

                    // Validation passed - use the validated data
                    updatedRow = validationResult.data || updatedRow;
                    console.log(
                      '✅ Validation passed, using validated data:',
                      updatedRow
                    );
                  }

                  // Get current data - handle both array and object formats
                  const currentData = readCurrentRows();

                  const newData = [...currentData];
                  newData[index] = updatedRow;

                  // Update form data - use the format expected by calculations
                  updateFormData({
                    [field.name]: { data: newData },
                    ...(isPartyDetailsTable
                      ? syncPartyDetailsAddMore(newData)
                      : {}),
                  });

                  console.log('✅ Table data updated (edit):', newData);
                } catch (error) {
                  console.error('❌ Error in onUpdateRow:', error);
                }
              },
              onDeleteRow: (index: number) => {
                const currentData = readCurrentRows();

                const newData = [...currentData];
                newData.splice(index, 1);

                // Update form data - use the format expected by calculations
                updateFormData({
                  [field.name]: {
                    data: newData,
                  },
                  ...(isPartyDetailsTable
                    ? syncPartyDetailsAddMore(newData)
                    : {}),
                });

                console.log('Table data updated (delete):', newData);
              },
              onEditRow: (
                updatedRow: any,
                index: number,
                context?: Record<string, unknown>
              ) => {
                if (isPartyDetailsTable) {
                  const prepared = preparePartyRow(updatedRow);
                  if (!prepared.success) {
                    return prepared;
                  }
                  Object.assign(updatedRow, prepared.data);
                }

                if (field.name === OVERTIME_WORKED_GRID_KEY) {
                  applyOvertimeWorkedRowAutomation(updatedRow);
                  const overtimeValidation =
                    validateOvertimeWorkedRow(updatedRow);
                  if (!overtimeValidation.success) {
                    return overtimeValidation;
                  }
                }

                return field.extras?.table?.onEditRow?.(
                  updatedRow,
                  index,
                  context
                );
              },
              onFormSchemaChange: (formData: Record<string, unknown>) => {
                if (field.name === OVERTIME_WORKED_GRID_KEY) {
                  applyOvertimeWorkedRowAutomation(formData);
                }

                // Latest Yes/No drives partyDetailsAddMore on the parent form
                // (TableField spreads this object after our callback).
                if (
                  isPartyDetailsTable &&
                  formData.additionalClientDetailsRequired !== undefined
                ) {
                  Object.assign(
                    formData,
                    buildPartyDetailsAddMoreFormUpdate(
                      formData.additionalClientDetailsRequired
                    )
                  );
                }

                field.extras?.table?.onFormSchemaChange?.(formData);
              },
            },
          },
        };
      }
      return field;
    },
    [formState, updateFormData]
  );

  return { processTableField };
};
